/* ==========================================================================
   VAN LYNN — product-form.js
   - Delegated AJAX add-to-cart for every form[data-product-form]
   - <variant-picker>: variant resolution, availability states, URL sync,
     and server-rendered price/inventory updates via Section Rendering API
   - <sticky-atc>: mobile/desktop sticky purchase bar
   ========================================================================== */
(function () {
  'use strict';
  const VL = (window.VL = window.VL || {});

  /* ---------- Add to cart ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    if (window.theme.settings.cartType !== 'drawer') return; // native POST → /cart

    e.preventDefault();
    const button = form.querySelector('[data-add-button]') || form.querySelector('[type="submit"]');
    if (!button || button.getAttribute('aria-disabled') === 'true' || button.disabled) return;
    const errorBox = form.querySelector('[data-form-error]') || form.closest('[data-product-info]')?.querySelector('[data-form-error]');

    // Native constraint validation for line-item properties (e.g. gift message).
    const invalid = Array.from(form.querySelectorAll('input, textarea, select')).find((f) => !f.checkValidity());
    if (invalid) {
      invalid.setAttribute('aria-invalid', 'true');
      invalid.reportValidity();
      return;
    }

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (errorBox) errorBox.hidden = true;

    try {
      const formData = new FormData(form);
      // Inputs associated via form="…" attribute (e.g. gift message outside the form element)
      if (form.id) {
        document.querySelectorAll(`[form="${form.id}"]`).forEach((field) => {
          if (field.name && !formData.has(field.name) && field.value) formData.append(field.name, field.value);
        });
      }
      await VL.cart.add(formData);
      const quickView = document.getElementById('QuickView');
      if (quickView && quickView.isOpen) quickView.close();
      VL.announce(window.theme.strings.addedToCart);
      VL.cart.open(button);
    } catch (err) {
      if (errorBox) {
        errorBox.textContent = err.message;
        errorBox.hidden = false;
      }
      VL.toast(err.message, 'error');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-index]')).map((group) => {
        const checked = group.querySelector('input:checked');
        const select = group.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      this.currentVariant = this.variants.find((v) => v.options.every((val, i) => val === options[i]));
      this.updateLabels(options);
      this.updateAvailability();
      const root = this.closest('[data-product-root]') || document;

      if (!this.currentVariant) {
        this.setUnavailable(root);
        return;
      }
      root.querySelectorAll('input[name="id"][data-variant-id-input], [data-variant-id-input]').forEach((input) => {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (this.updateUrl) {
        window.history.replaceState({}, '', `${this.productUrl}?variant=${this.currentVariant.id}`);
      }
      root.dispatchEvent(new CustomEvent('vl:variant-change', {
        bubbles: true,
        detail: { variant: this.currentVariant, sectionId: this.sectionId },
      }));
      this.renderSection(root);
    }

    updateLabels(options) {
      this.querySelectorAll('[data-option-index]').forEach((group, i) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = options[i] || '';
      });
    }

    /* Mark option values that cannot form an available variant with the other current selections. */
    updateAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('input[type="radio"], option').forEach((el) => {
          const value = el.value;
          const match = this.variants.filter((v) =>
            v.options[index] === value && v.options.every((val, i) => i === index || val === selected[i])
          );
          const available = match.some((v) => v.available);
          const exists = match.length > 0;
          if (el.tagName === 'OPTION') {
            el.textContent = el.dataset.label + (exists && !available ? ` — ${window.theme.strings.soldOut}` : !exists ? ` — ${window.theme.strings.unavailable}` : '');
          } else {
            el.closest('.swatch')?.classList.toggle('is-unavailable', !available);
            el.setAttribute('aria-describedby', !available ? `${this.id}-unavailable` : '');
            if (available) el.removeAttribute('aria-describedby');
          }
        });
      });
    }

    setUnavailable(root) {
      root.querySelectorAll('[data-add-button]').forEach((btn) => {
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
        const label = btn.querySelector('[data-add-label]');
        if (label) label.textContent = window.theme.strings.unavailable;
      });
      root.querySelectorAll('[data-variant-update="price"]').forEach((el) => el.classList.add('is-unavailable'));
    }

    async renderSection(root) {
      const id = this.currentVariant.id;
      this.abort?.abort();
      this.abort = new AbortController();
      root.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${this.productUrl}?variant=${id}&section_id=${this.sectionId}`, { signal: this.abort.signal });
        const html = VL.parseHTML(await res.text());
        if (this.currentVariant.id !== id) return;
        root.querySelectorAll('[data-variant-update]').forEach((el) => {
          const key = el.dataset.variantUpdate;
          const fresh = html.querySelector(`[data-variant-update="${key}"]`);
          if (fresh) {
            el.innerHTML = fresh.innerHTML;
            el.classList.remove('is-unavailable');
            el.hidden = fresh.hidden;
          }
        });
        // Buy buttons: reflect availability of the chosen variant.
        root.querySelectorAll('[data-add-button]').forEach((btn) => {
          const available = this.currentVariant.available;
          btn.disabled = !available;
          btn.setAttribute('aria-disabled', available ? 'false' : 'true');
          const label = btn.querySelector('[data-add-label]');
          if (label) label.textContent = available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
        });
        root.querySelectorAll('[data-dynamic-checkout]').forEach((el) => {
          el.hidden = !this.currentVariant.available;
        });
        document.dispatchEvent(new CustomEvent('vl:content-loaded', { detail: { root } }));
      } catch (err) {
        if (err.name !== 'AbortError') VL.toast(window.theme.strings.cartError, 'error');
      } finally {
        root.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const root = this.closest('[data-product-root]') || document;
      const target = root.querySelector('[data-main-buy-buttons]');
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', passed ? 'false' : 'true');
        this.toggleAttribute('inert', !passed);
      });
      this.observer.observe(target);
      this.setAttribute('inert', '');
    }
    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
})();
